import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth, STAFF_ROLES } from '@structural-climbing/auth'
import type { StaffRole } from '@structural-climbing/auth'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
} from '@structural-climbing/ui'

interface InviteStaffModalProps {
  isOpen: boolean
  onClose: () => void
  onInvited: () => void
}

export function InviteStaffModal({ isOpen, onClose, onInvited }: InviteStaffModalProps) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const ROLE_LABELS: Record<StaffRole, string> = {
    front_desk: t('team.roleFrontDesk'),
    route_setter: t('team.roleRouteSetter'),
    trainer: t('team.roleTrainer'),
  }
  const ROLE_OPTIONS = STAFF_ROLES.map((key) => ({ key, label: ROLE_LABELS[key] }))

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [staffRole, setStaffRole] = useState<StaffRole>('front_desk')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  function handleClose() {
    setName('')
    setEmail('')
    setStaffRole('front_desk')
    setError(null)
    setSent(false)
    onClose()
  }

  function handleDone() {
    setName('')
    setEmail('')
    setStaffRole('front_desk')
    setError(null)
    setSent(false)
    onInvited()
  }

  async function handleSubmit() {
    if (!name.trim() || !email.trim()) return
    setLoading(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const resp = await fetch(`${apiUrl}/invite-staff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), staff_role: staffRole }),
      })
      if (!resp.ok) {
        const { error: msg, code } = (await resp.json()) as { error?: string; code?: string }
        if (code === 'EMAIL') throw new Error(t('team.errorInviteEmail'))
        if (code === 'ALREADY_ON_TEAM') throw new Error(t('team.errorAlreadyOnTeam'))
        throw new Error(msg ?? t('team.errorInvite'))
      }
      setSent(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('team.errorInvite'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      scrollBehavior="inside"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose()
      }}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader>{t('team.inviteTitle')}</ModalHeader>
            <ModalBody>
              {sent ? (
                <p className="py-2 text-sm text-success">{t('common.inviteSentTo', { email })}</p>
              ) : (
                <div className="flex flex-col gap-4">
                  <Input
                    label={t('common.name')}
                    placeholder={t('common.namePlaceholder')}
                    value={name}
                    onValueChange={setName}
                    autoFocus
                  />
                  <Input
                    label={t('common.email')}
                    placeholder={t('common.emailPlaceholder')}
                    type="email"
                    value={email}
                    onValueChange={setEmail}
                  />
                  <Select
                    label={t('team.roleLabel')}
                    selectedKeys={[staffRole]}
                    onSelectionChange={(keys) => {
                      const val = Array.from(keys)[0] as StaffRole
                      if (val) setStaffRole(val)
                    }}
                  >
                    {ROLE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.key}>{opt.label}</SelectItem>
                    ))}
                  </Select>
                  {error && <p className="text-sm text-danger">{error}</p>}
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              {sent ? (
                <Button color="primary" onClick={handleDone}>
                  {t('common.done')}
                </Button>
              ) : (
                <>
                  <Button variant="flat" onClick={handleClose} isDisabled={loading}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    color="primary"
                    onClick={handleSubmit}
                    isLoading={loading}
                    isDisabled={!name.trim() || !email.trim()}
                  >
                    {t('common.sendInvite')}
                  </Button>
                </>
              )}
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
